import React, { useEffect, useMemo, useState } from 'react';
import T from 'prop-types';
import { isEmpty, pick } from 'lodash-es';

import FormModalContent from './FormModalContent';
import ConfirmationModalContent from './ConfirmationModalContent';
import PageModal from '../../../../Modals/PageModal';
import { toast } from '../../../../Toasts';
import api from '../../../api';
import { decorateUpsertExportRequestParams } from '../../../utils';
import { generateDateTimeSuffix } from '../../../../../utils/helpers';
import { addQuarters, startOfQuarter, today } from '../../../../../utils/date';
import { getGlobalSettings } from '../../../../../utils/global_settings';

const isCurrentUserRecipient = (recipients) => {
  const { currentUser } = getGlobalSettings();
  if (isEmpty(currentUser)) return false;

  return recipients.some((r) => r.id === currentUser.id);
};

const preselectCurrentUserAsRecipient = () => {
  const { currentUser } = getGlobalSettings();
  if (isEmpty(currentUser)) return [];

  // Save the same structure as response from the API
  return [
    {
      id: currentUser.id,
      kind: 'user',
      label: `${currentUser.fullName} (${currentUser.email})`,
      text: `${currentUser.fullName} (${currentUser.email})`,
      value: currentUser.email,
    },
  ];
};

const defaultExtraReportState = (report) => ({
  name: generateDateTimeSuffix(),
  schedule_expires_at: startOfQuarter(addQuarters(today(), 1)),
  frequency: 'weekly',
  recipients: report.email_notification ? preselectCurrentUserAsRecipient() : [],
});

const ScheduleExportModal = ({ open, report, onClose, onBackToEdit, onSuccess }) => {
  const [step, setStep] = useState('form'); // 'form' | 'confirmation'
  const [extraReportState, setExtraReportState] = useState(defaultExtraReportState(report));

  const [isProcessing, setIsProcessing] = useState(false);

  const extendedReport = useMemo(
    () => ({ ...report, ...extraReportState }),
    [report, extraReportState]
  );

  const handleConfirm = () => {
    setIsProcessing(true);

    const requestData = decorateUpsertExportRequestParams(report, {
      ...pick(extraReportState, ['name', 'schedule_expires_at', 'frequency']),
      recipient_ids: extraReportState.recipients.map((r) => r.id),
      email_notification: isCurrentUserRecipient(extraReportState.recipients),
    });

    const request = !report.id
      ? api.createCustomExport(requestData)
      : api.updateCustomExport(report.id, { ...requestData, no_resync: true });

    request
      .then(() => {
        setIsProcessing(false);
        onSuccess(requestData);
      })
      .catch(() => {
        setIsProcessing(false);
        toast.error('Oops! Something went wrong. Please, try again later');
      });
  };

  // Effects
  useEffect(() => {
    const initialState = defaultExtraReportState(report);

    if (open) {
      // If report has been previously scheduled, populate form with existing report values. Otherwise, use defaults.
      setExtraReportState({
        name: report?.id ? report.name : initialState.name,
        schedule_expires_at: report?.schedule_expires_at || initialState.schedule_expires_at,
        frequency: report?.frequency || initialState.frequency,
        recipients: isEmpty(report?.recipients) ? initialState.recipients : report.recipients,
      });
    } else {
      // Reset step to 'form' on modal close and clear extra report state
      setStep('form');
      setExtraReportState(initialState);
    }
  }, [open]);

  return (
    <PageModal
      open={open}
      shouldCloseOnOverlayClick={false}
      closeButtonVersionButtonSize="lg"
      hideCloseButton={step === 'confirmation'}
      className="schedule-export-modal"
      header="Schedule Export"
      main={
        step === 'form' ? (
          <FormModalContent
            extraReportState={extraReportState}
            setExtraReportState={setExtraReportState}
            setStep={setStep}
            onBackToEdit={onBackToEdit || onClose}
          />
        ) : (
          <ConfirmationModalContent
            isProcessing={isProcessing}
            report={extendedReport}
            setStep={setStep}
            onConfirm={handleConfirm}
          />
        )
      }
      onClose={onClose}
    />
  );
};

ScheduleExportModal.propTypes = {
  open: T.bool.isRequired,
  report: T.object.isRequired,
  onClose: T.func.isRequired,
  onBackToEdit: T.func,
  onSuccess: T.func,
};

export default ScheduleExportModal;
